import { useEffect, useState } from 'react';
import { useCrucibleClient } from '../transport/context';

/**
 * #2058 — the in-chat build-time asset-review / steering surface trigger.
 *
 * Returns the gameId for which the steering panel should be mounted (or `null`),
 * for the currently-focused game. The panel must mount RELIABLY regardless of
 * event timing — this was the #1 north-star bug: the build-time review fires
 * `asset-review:ready` ONE-SHOT, and in the normal auto-build ordering (create →
 * navigate → build kickoff fires the review) that push can land BEFORE the
 * Workspace subscribes, so the event is lost and the panel never mounts. The
 * game then ships placeholder art because the user approved nothing.
 *
 * The fix is two paths, so the panel surfaces with NO timing dependency:
 *   1. LIVE push — subscribe to `asset-review:ready` (the fast path for a review
 *      that opens after we've mounted).
 *   2. MOUNT PROBE — query the buffered active review via `asset-review:active`
 *      (the recovery path for a review that opened before we subscribed). Main
 *      buffers `{ gameId, reviewUrl }` whenever a build-time review server binds,
 *      so this recovers a missed push deterministically.
 *
 * Both filter by the focused `gameId` so a background game's review never
 * hijacks the panel. The state clears on `gameId` change (and the caller clears
 * it on the engine `done` signal). Mirrors the soul-intake mount-probe pattern
 * that solves the identical race for the interview surface.
 *
 * Inert when `enabled` is false (the `interviewIntegrated` flag is off): the
 * legacy `BuildReviewOverlay` modal owns the review surface in that case.
 *
 * @param gameId  the focused game, or null/undefined when no game is open.
 * @param enabled gate on the `interviewIntegrated` flag.
 * @returns the gameId whose steering panel should mount, or null.
 */
export function useAssetReviewSurface(
  gameId: string | null | undefined,
  enabled: boolean,
  placement: 'local' | 'cloud' = 'local',
): string | null {
  const client = useCrucibleClient();
  const [activeGameId, setActiveGameId] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setActiveGameId(null);
      return;
    }
    let cancelled = false;
    let durableSurfaceActive = false;
    setActiveGameId(null); // clear on game change

    // (1) LIVE push — a review that opens AFTER we subscribe.
    const unsub = client.subscribe('asset-review:ready', (event) => {
      if (gameId && event.gameId !== gameId) return;
      if (!cancelled) {
        durableSurfaceActive = false;
        setActiveGameId(event.gameId);
      }
    });

    // (2) MOUNT PROBE — recover a review that opened BEFORE we subscribed (the
    // one-shot `asset-review:ready` may have already fired and been dropped).
    // crucible#2611 Bugbot HIGH (round 5): pass OUR OWN gameId so main returns
    // THIS game's record specifically. crucible#3007 — rewired off the legacy
    // `asset-review:active` (optional gameId, cloud-unreachable — no furnace
    // handler for it) onto `art:active` (the cloud-reachable twin), which
    // requires an explicit gameId on every call — matching this hook's own
    // stated intent ("Both filter by the focused gameId so a background
    // game's review never hijacks the panel"). With no focused gameId there
    // is nothing to filter by, so the probe is skipped entirely rather than
    // recovering an unscoped "most recent" review.
    if (gameId) {
      const durableStatusAvailable = placement === 'local';
      let statusTimer: ReturnType<typeof setInterval> | undefined;
      let statusProbeSequence = 0;
      const stopStatusPolling = (): void => {
        if (statusTimer) clearInterval(statusTimer);
        statusTimer = undefined;
      };
      const probeDurableStatus = async (): Promise<void> => {
        if (!durableStatusAvailable) return;
        const probeSequence = ++statusProbeSequence;
        try {
          const status = await client.invoke('build-asset-review:status', { gameId });
          if (cancelled || probeSequence !== statusProbeSequence) return;
          if (status.state !== 'none' && status.state !== 'complete') {
            durableSurfaceActive = true;
            setActiveGameId(gameId);
          } else if (durableSurfaceActive) {
            durableSurfaceActive = false;
            setActiveGameId((active) => (active === gameId ? null : active));
          }
        } catch {
          // Durable review work may appear after an initial `none`, and a
          // transient transport failure must not permanently disable recovery.
          // Keep polling until this workspace unmounts or a live/active review
          // takes ownership of the surface.
        }
      };
      void (async () => {
        try {
          const active = await client.invoke('art:active', { gameId });
          if (cancelled) return;
          if (!active) {
            if (durableStatusAvailable) await probeDurableStatus();
            return;
          }
          if (active.gameId !== gameId) return;
          durableSurfaceActive = false;
          setActiveGameId(active.gameId);
          stopStatusPolling();
        } catch {
          if (durableStatusAvailable) await probeDurableStatus();
        }
      })();
      if (durableStatusAvailable) {
        statusTimer = setInterval(() => void probeDurableStatus(), 1500);
      }

      return () => {
        cancelled = true;
        stopStatusPolling();
        unsub();
      };
    }

    return () => {
      cancelled = true;
      unsub();
    };
  }, [client, gameId, enabled, placement]);

  return enabled ? activeGameId : null;
}
